import { useEffect, useState } from "react";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useUser } from "@/lib/useUser";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DollarSign, FileText, Clock, CheckCircle, XCircle, Eye, TrendingUp } from "lucide-react";
import { NavLink } from "react-router-dom";

// CORRIGÉ — "pending"/"accepted" n'étaient jamais réellement écrits nulle
// part : lender-offers.controller.ts écrit 'submitted' à la création, et
// AdminLendersOffers.tsx (l'outil de gouvernance staff, avec de vrais
// boutons Approuver/Rejeter) écrit 'approved'/'rejected'. Avec l'ancien
// vocabulaire, toute offre réelle affichait un statut non reconnu — et
// STATUS_CONFIG[offer.status] renvoyait undefined, plantant sur
// `cfg.icon` dès la première offre chargée. Renommé pour correspondre
// aux vraies valeurs, sans toucher à AdminLendersOffers.tsx (déjà
// fonctionnel, plus risqué à modifier).
type OfferStatus = "submitted" | "under_review" | "approved" | "rejected" | "countered";

interface LoanOffer {
  id: string;
  dealId: string;
  businessName: string;
  askingPrice: number;
  loanAmount: number;
  interestRate: number;
  termMonths: number;
  status: OfferStatus;
  submittedAt: { toDate: () => Date } | null;
  updatedAt: { toDate: () => Date } | null;
  notes?: string;
}

const STATUS_CONFIG: Record<OfferStatus, { labelEn: string; labelFr: string; color: string; icon: React.ElementType }> = {
  submitted:     { labelEn: "Submitted",      labelFr: "Soumise",       color: "bg-amber-50 text-amber-800 border-amber-200",    icon: Clock         },
  under_review:  { labelEn: "Under Review",   labelFr: "En révision",   color: "bg-blue-50 text-blue-800 border-blue-200",       icon: Eye           },
  approved:      { labelEn: "Approved",       labelFr: "Approuvée",     color: "bg-green-50 text-green-800 border-green-200",    icon: CheckCircle   },
  rejected:      { labelEn: "Rejected",       labelFr: "Refusée",       color: "bg-red-50 text-red-800 border-red-200",          icon: XCircle       },
  countered:     { labelEn: "Counter-offer",  labelFr: "Contre-offre",  color: "bg-purple-50 text-purple-800 border-purple-200", icon: TrendingUp    },
};

function fmt(n: number) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function fmtDate(ts: { toDate: () => Date } | null, locale: string) {
  if (!ts) return "—";
  return ts.toDate().toLocaleDateString(locale === "fr" ? "fr-CA" : "en-US", { year: "numeric", month: "short", day: "numeric" });
}

export default function LoanOffers() {
  const { user } = useUser();
  const { language } = useLanguage();
  const isFr = language === "fr";
  const [offers, setOffers] = useState<LoanOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<OfferStatus | "all">("all");

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, "lenderOffers"),
      where("lenderId", "==", user.uid),
      orderBy("submittedAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setOffers(snap.docs.map(d => ({ id: d.id, ...d.data() } as LoanOffer)));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [user?.uid]);

  const filtered = filter === "all" ? offers : offers.filter(o => o.status === filter);

  const stats = {
    total:    offers.length,
    pending:  offers.filter(o => o.status === "submitted" || o.status === "under_review").length,
    accepted: offers.filter(o => o.status === "approved").length,
    totalValue: offers.filter(o => o.status === "approved").reduce((s, o) => s + o.loanAmount, 0),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium" style={{ color: "#1D314D" }}>
            {isFr ? "Mes Offres de Prêt" : "My Loan Offers"}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isFr ? "Suivez vos offres soumises et leur statut" : "Track your submitted offers and their status"}
          </p>
        </div>
        <NavLink to="/lender/submit-offer">
          <Button style={{ background: "#1D314D", color: "white" }} size="sm" className="gap-2">
            <DollarSign className="h-4 w-4" />
            {isFr ? "Nouvelle offre" : "New Offer"}
          </Button>
        </NavLink>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: isFr ? "Total" : "Total",           value: stats.total,                  sub: isFr ? "offres" : "offers"   },
          { label: isFr ? "En cours" : "In Progress",  value: stats.pending,                sub: isFr ? "actives" : "active"   },
          { label: isFr ? "Approuvées" : "Approved",   value: stats.accepted,               sub: isFr ? "offres" : "offers"   },
          { label: isFr ? "Valeur engagée" : "Committed Value", value: fmt(stats.totalValue), sub: isFr ? "approuvé" : "approved" },
        ].map((s, i) => (
          <Card key={i} className="border-0 bg-muted/40">
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
              <p className="text-2xl font-medium" style={{ color: "#1D314D" }}>{s.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {(["all", "submitted", "under_review", "approved", "countered", "rejected"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="text-xs px-3 py-1.5 rounded-full border transition-all"
            style={{
              background: filter === f ? "#1D314D" : "transparent",
              color: filter === f ? "white" : "var(--color-text-secondary)",
              borderColor: filter === f ? "#1D314D" : "var(--color-border-tertiary)",
            }}
          >
            {f === "all"
              ? (isFr ? "Toutes" : "All")
              : isFr
                ? STATUS_CONFIG[f as OfferStatus].labelFr
                : STATUS_CONFIG[f as OfferStatus].labelEn}
            {f !== "all" && (
              <span className="ml-1.5 opacity-60">
                {offers.filter(o => o.status === f).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Offers list */}
      {loading ? (
        <div className="text-sm text-muted-foreground py-12 text-center">
          {isFr ? "Chargement..." : "Loading..."}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm font-medium text-muted-foreground">
              {isFr ? "Aucune offre trouvée" : "No offers found"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {isFr ? "Soumettez votre première offre de prêt" : "Submit your first loan offer to get started"}
            </p>
            <NavLink to="/lender/submit-offer">
              <Button variant="outline" size="sm" className="mt-4">
                {isFr ? "Soumettre une offre" : "Submit an Offer"}
              </Button>
            </NavLink>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((offer) => {
            const cfg = STATUS_CONFIG[offer.status];
            const Icon = cfg?.icon || Clock;
            return (
              <Card key={offer.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-medium truncate" style={{ color: "#1D314D" }}>
                          {offer.businessName || (isFr ? "Entreprise confidentielle" : "Confidential Business")}
                        </h3>
                        <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-medium ${cfg?.color || "bg-muted text-muted-foreground border-border"}`}>
                          <Icon className="h-3 w-3" />
                          {cfg ? (isFr ? cfg.labelFr : cfg.labelEn) : offer.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-4 mt-3">
                        {[
                          { label: isFr ? "Prix demandé" : "Asking Price", value: fmt(offer.askingPrice) },
                          { label: isFr ? "Montant prêt" : "Loan Amount",  value: fmt(offer.loanAmount)  },
                          { label: isFr ? "Taux" : "Rate",                 value: `${offer.interestRate}%` },
                          { label: isFr ? "Durée" : "Term",                value: `${offer.termMonths} ${isFr ? "mois" : "mo."}` },
                        ].map((f, i) => (
                          <div key={i}>
                            <p className="text-[10px] text-muted-foreground">{f.label}</p>
                            <p className="text-sm font-medium mt-0.5">{f.value}</p>
                          </div>
                        ))}
                      </div>
                      {offer.notes && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-1">{offer.notes}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] text-muted-foreground">
                        {isFr ? "Soumise" : "Submitted"}
                      </p>
                      <p className="text-xs mt-0.5">{fmtDate(offer.submittedAt, language)}</p>
                      {offer.updatedAt && (
                        <>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            {isFr ? "Mise à jour" : "Updated"}
                          </p>
                          <p className="text-xs mt-0.5">{fmtDate(offer.updatedAt, language)}</p>
                        </>
                      )}
                      <NavLink to={`/lender/deal-preview/${offer.dealId}`}>
                        <Button variant="ghost" size="sm" className="mt-2 h-7 text-xs gap-1">
                          <Eye className="h-3 w-3" />
                          {isFr ? "Voir" : "View"}
                        </Button>
                      </NavLink>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}